import * as DialogPrimitive from '@radix-ui/react-dialog';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { cn } from '@/lib/cn';

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogPortal = DialogPrimitive.Portal;
export const DialogClose = DialogPrimitive.Close;

export const DialogOverlay = forwardRef<
    React.ElementRef<typeof DialogPrimitive.Overlay>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => {
    return (
        <DialogPrimitive.Overlay
            ref={ref}
            className={cn(
                'fixed inset-0 z-[70] bg-tg-ink/40 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
                className,
            )}
            {...props}
        />
    );
});
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

export function DialogContent({
    className,
    children,
    ...props
}: DialogPrimitive.DialogContentProps): JSX.Element {
    const { t } = useTranslation();

    return (
        <DialogPortal>
            <DialogOverlay />
            <DialogPrimitive.Content
                className={cn(
                    // Phone (<sm): full-width bottom sheet, capped at 92dvh, safe-area aware.
                    // sm+: centred card. Physical left/translate is deliberate: it centres
                    // identically in LTR and RTL (a logical start offset plus -translate-x drifts off-screen in RTL).
                    // grid-cols-1 is minmax(0,1fr): a wide/nowrap child (table, long id) scrolls inside its own
                    // box instead of growing the implicit auto track past the sheet edge.
                    // max-w is unprefixed so a caller's max-w-md / max-w-lg wins from sm up; max-sm:max-w-none
                    // keeps the phone sheet edge to edge (a capped inset-x-0 sheet hugs one edge on 390-430px phones).
                    'fixed inset-x-0 bottom-0 z-[70] grid max-h-[92dvh] w-full max-w-[640px] grid-cols-1 gap-4 max-sm:gap-x-0 max-sm:[&>*:not(:last-child)]:col-start-1 overflow-y-auto overscroll-contain rounded-t-2xl border border-tg-border bg-white p-5 pb-[max(1.25rem,var(--pa-safe-bottom))] shadow-[var(--tg-shadow-pop)] focus:outline-none',
                    'sm:inset-x-auto sm:bottom-auto sm:left-1/2 sm:top-1/2 sm:w-[calc(100vw-2rem)] max-sm:max-w-none sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-2xl sm:p-6',
                    className,
                )}
                {...props}
            >
                {children}
                {/*
                 * Phone sheet scrolls, so the close button rides a 0x0 sticky anchor pinned to the top-end
                 * corner of the content box. The anchor sits in an implicit second column of row 1 (column
                 * gap 0 on phones) so it adds no row and no gap; DOM order keeps Radix's initial focus on
                 * the content. Every other child is pinned to column 1, or auto-placement would fill column 2 too.
                 * From sm the anchor is display:contents and the button is absolute as before.
                 */}
                <div className="z-10 max-sm:sticky max-sm:top-0 max-sm:col-start-2 max-sm:row-start-1 max-sm:h-0 max-sm:w-0 sm:contents">
                    <DialogPrimitive.Close
                        aria-label={t('common.close', { defaultValue: 'Close' })}
                        className="absolute end-0 top-0 grid h-11 w-11 place-items-center rounded-lg bg-white/90 text-tg-ink/70 sm:end-3 sm:top-3 sm:h-9 sm:w-9 sm:bg-transparent sm:text-current sm:opacity-70 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                        <X className="h-4 w-4" />
                    </DialogPrimitive.Close>
                </div>
            </DialogPrimitive.Content>
        </DialogPortal>
    );
}

export function DialogHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>): JSX.Element {
    return <div className={cn('flex flex-col gap-1.5 pe-10', className)} {...props} />;
}

export function DialogFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>): JSX.Element {
    return (
        <div
            className={cn(
                // Sticky on the phone sheet so actions stay reachable while the body scrolls.
                'sticky bottom-0 -mx-5 -mb-[max(1.25rem,var(--pa-safe-bottom))] flex flex-col-reverse gap-2 border-t border-tg-border bg-white px-5 pb-[max(1.25rem,var(--pa-safe-bottom))] pt-3 sm:static sm:mx-0 sm:mb-0 sm:flex-row sm:flex-wrap sm:justify-end sm:border-0 sm:p-0 [&>button]:min-h-11 sm:[&>button]:min-h-0',
                className,
            )}
            {...props}
        />
    );
}

export function DialogTitle({
    className,
    ...props
}: DialogPrimitive.DialogTitleProps): JSX.Element {
    return (
        <DialogPrimitive.Title
            className={cn('text-lg font-semibold leading-tight tracking-tight [overflow-wrap:anywhere]', className)}
            {...props}
        />
    );
}

export function DialogDescription({
    className,
    ...props
}: DialogPrimitive.DialogDescriptionProps): JSX.Element {
    return (
        <DialogPrimitive.Description
            className={cn('text-sm text-muted-foreground [overflow-wrap:anywhere]', className)}
            {...props}
        />
    );
}
